import { Sphere } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { setMeasurementLines } from '@store/reducers/viewerSlice';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Group, type Object3D, type Object3DEventMap, Raycaster, Vector2, Vector3 } from 'three';
import { Line2, LineGeometry, LineMaterial } from 'three-stdlib';
import createLabelSprite from './createLabelSprite';

export interface Measurement {
  start: [number, number, number];
  end?: [number, number, number];
  isCompleted: boolean;
  distance?: number;
}

const Measurement = () => {
  const { project, isMeasuring, measurementColor, measurementLines } = useAppSelector((state) => state.viewer);
  const dispatch = useAppDispatch();

  const model = project?.model as Object3D<Object3DEventMap>;

  const { camera } = useThree();
  const raycaster = new Raycaster();
  const [isClicking, setIsClicking] = useState(false);
  const [mouse, setMouse] = useState(new Vector2());

  const lineGroupRefs = useRef<Array<Group | null>>([]);

  const startNewLine = (newPoint: [number, number, number]) => {
    if (measurementLines.length > 0 && !measurementLines[measurementLines.length - 1].isCompleted) {
      const lastMeasurementLine = measurementLines[measurementLines.length - 1];
      const updatedLastLine: Measurement = { ...lastMeasurementLine, isCompleted: false };
      dispatch(setMeasurementLines([...measurementLines.slice(0, -1), updatedLastLine]));
    } else {
      const newLine: Measurement = { start: newPoint, isCompleted: false };
      dispatch(setMeasurementLines([...measurementLines, newLine]));
    }
  };

  const handleModelClick = (vectorEvent: Vector3) => {
    const newPoint = [vectorEvent.x, vectorEvent.y, vectorEvent.z] as [number, number, number];

    if (measurementLines.length > 0) {
      const lastMeasurement = measurementLines[measurementLines.length - 1];

      if (!lastMeasurement.isCompleted) {
        const updatedLines = measurementLines.map((measurement, index) => {
          if (index === measurementLines.length - 1) {
            return { ...measurement, end: newPoint, isCompleted: true, distance: calculateDistance(measurement.start, newPoint) };
          } else {
            return measurement;
          }
        });

        dispatch(setMeasurementLines(updatedLines));
      } else {
        startNewLine(newPoint);
      }
    } else {
      startNewLine(newPoint);
    }
  };

  const calculateDistance = (start: [number, number, number], end: [number, number, number]): number => {
    const vectorStart = new Vector3(...start);
    const vectorEnd = new Vector3(...end);
    return vectorStart.distanceTo(vectorEnd);
  };

  const onClick = (event: MouseEvent) => {
    event.preventDefault();

    const { offsetX, offsetY, target } = event;

    const rect = (target as HTMLElement).getBoundingClientRect();
    const x = ((offsetX - rect.left) / rect.width) * 2 - 1;
    const y = -((offsetY - rect.top) / rect.height) * 2 + 1;

    setMouse(new Vector2(x, y));
    setIsClicking(true);
  };

  useEffect(() => {
    const eventName = 'click';
    const canvas = document.querySelector('#threejs-canvas') as HTMLCanvasElement;

    canvas.removeEventListener(eventName, onClick);

    if (isMeasuring) {
      canvas.addEventListener(eventName, onClick);
    }

    return () => {
      canvas.removeEventListener(eventName, onClick);
    };
  }, [isMeasuring]);

  useEffect(() => {
    const applyLines = () => {
      measurementLines.forEach((measurement, index) => {
        const group = lineGroupRefs.current[index];
        if (measurement.isCompleted) {
          if (group) {
            group.clear();
          }

          const positions = [measurement.start, measurement.end].map(point => new Vector3(...point ?? []));
          const pts: number[] = [];

          for (const pt of positions) {
            pts.push(pt.x, pt.y, pt.z);
          }

          const geometry = new LineGeometry();
          geometry.setPositions(pts);

          const resolution = new Vector2(window.innerWidth, window.innerHeight);

          const material = new LineMaterial({
            color: measurementColor,
            resolution,
            linewidth: 2,
            transparent: true,
            depthTest: false,
            depthWrite: false,
          });

          const line = new Line2(geometry, material);
          line.computeLineDistances();

          const lineGroup = new Group();
          lineGroup.add(line);

          const midPoint = new Vector3().addVectors(positions[0], positions[1]).multiplyScalar(0.5);
          const labelSprite = createLabelSprite(measurement.distance ?? 0, midPoint);
          lineGroup.add(labelSprite);
          group?.add(lineGroup);
        }
      });
    };

    applyLines();
  }, [measurementLines, measurementColor]);

  useFrame(() => {
    if (isClicking) {

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(model.children, true);

      if (intersects.length > 0) {
        handleModelClick(intersects[0].point);
      }
      setIsClicking(false);
    }
  });

  return (
    <>
      {
        measurementLines.map((measurement, lineIndex) => (
          <Fragment key={lineIndex}>
            <Sphere key={0} position={measurement.start} args={[0.007, 32, 32]} >
              <meshBasicMaterial color={measurementColor} depthWrite={false} depthTest={false} />
            </Sphere>
            {measurement.end != null ? <Sphere key={1} position={measurement.end} args={[0.007, 32, 32]}>
              <meshBasicMaterial color={measurementColor} depthWrite={false} depthTest={false} />
            </Sphere> : <></>}
            <group ref={(ref) => (lineGroupRefs.current[lineIndex] = ref)} />
          </Fragment>
        ))
      }
    </>
  );
};

export default Measurement;
